import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, ArrowRight, Sparkles, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { Widget, WidgetEmpty } from "@/components/dashboard/Widget";
import { TrendPill } from "@/components/dashboard/TrendPill";
import { Button } from "@/components/ui/button";
import { useCurrency, useCurrencyChangeListener } from "@/context/CurrencyContext";

const PERCENTILES = ["p25", "p50", "p75", "p90"];
const PERCENTILE_LABEL = { p25: "P25", p50: "P50", p75: "P75", p90: "P90" };

/** Salary Intelligence Panel — current salary vs market band. */
export const SalaryIntelligenceWidget = ({ size = "lg" }) => {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: false });
  const { currency: userCurrency, format: userFormat, ready: currencyReady } = useCurrency();

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    return api
      .get("/dashboard/salary")
      .then((r) => setState({ loading: false, data: r.data, error: false }))
      .catch(() => setState({ loading: false, data: null, error: true }));
  };

  useEffect(() => {
    let alive = true;
    api
      .get("/dashboard/salary")
      .then((r) => alive && setState({ loading: false, data: r.data, error: false }))
      .catch(() => alive && setState({ loading: false, data: null, error: true }));
    return () => { alive = false; };
  }, []);

  // Refetch when the user switches currency — backend returns amounts + a
  // currency field per the user's saved profile, so we must round-trip.
  useCurrencyChangeListener(load);

  const d = state.data;

  // Position on the 0..100% axis — clamped so the marker never overflows.
  const markerPct = useMemo(() => {
    if (!d?.band || d.current_salary == null) return null;
    const min = d.band.p25 * 0.7; // 30% below p25
    const max = d.band.p90 * 1.05;
    const pct = ((d.current_salary - min) / (max - min)) * 100;
    return Math.max(2, Math.min(98, pct));
  }, [d]);

  const bandTick = (key) => {
    if (!d?.band) return null;
    const min = d.band.p25 * 0.7;
    const max = d.band.p90 * 1.05;
    const pct = ((d.band[key] - min) / (max - min)) * 100;
    return Math.max(2, Math.min(98, pct));
  };

  const locale = typeof navigator !== "undefined" ? navigator.language : "en-US";

  // The backend returns amounts in the user's canonical reporting currency
  // (`d.currency`). Prefer that. Never fall back to a hardcoded "USD".
  const ccyForData = d?.currency || userCurrency;

  const currencyFmt = (value) => {
    if (value == null) return "—";
    if (!currencyReady && !d?.currency) return "…";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ccyForData,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return userFormat(value, { currency: ccyForData, decimals: 0 });
    }
  };

  const compactFmt = (value) => {
    if (value == null) return "—";
    if (!currencyReady && !d?.currency) return "…";
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ccyForData,
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value);
    } catch {
      return userFormat(value, { currency: ccyForData, compact: true });
    }
  };

  return (
    <Widget
      title="Salary Intelligence"
      icon={TrendingUp}
      size={size}
      testId="salary-intel-widget"
      viewAllTo="/app/salary"
      viewAllLabel="Full report"
    >
      {state.loading && (
        <div className="space-y-3">
          <div className="skeleton h-6 w-40" />
          <div className="skeleton h-2 w-full mt-4" />
          <div className="skeleton h-10 w-full mt-4" />
          <div className="skeleton h-8 w-32 mt-2" />
        </div>
      )}

      {!state.loading && (state.error || !d) && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Couldn&apos;t load salary data.
        </div>
      )}

      {d?.state === "no_data" && (
        <WidgetEmpty
          testId="salary-intel-empty"
          title={
            d.has_current_salary
              ? "Run salary intelligence to see your market band"
              : "Add your salary to unlock market intelligence"
          }
          description={
            d.has_current_salary
              ? "AI benchmarks your role against your local market — P25 → P90 in seconds."
              : "We'll compare your compensation to your local market and show what you're leaving on the table."
          }
          action={
            <Button
              size="sm"
              onClick={() => nav(d.cta?.path || "/app/salary")}
              className="gap-1.5"
              data-testid="salary-intel-empty-cta"
            >
              {d.cta?.label || "Get started"} <ArrowRight size={13} strokeWidth={2} />
            </Button>
          }
        />
      )}

      {d?.state === "ok" && (
        <>
          {/* Top row — current salary + percentile placement */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                Current salary{d.market_label ? ` · ${d.market_label}` : ""}
              </div>
              <div className="flex items-baseline gap-2">
                <span
                  className="font-display font-black text-3xl text-foreground"
                  data-testid="salary-intel-current"
                >
                  {currencyFmt(d.current_salary)}
                </span>
                {d.trend?.current_salary && (
                  <TrendPill
                    delta={d.trend.current_salary.delta}
                    period={
                      d.trend.current_salary.period_days
                        ? `${d.trend.current_salary.period_days}d`
                        : undefined
                    }
                    format="currency"
                    currencyFormat={compactFmt}
                    testId="salary-intel-current-trend"
                  />
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                Competitiveness
              </div>
              <div className="flex items-center justify-end gap-1.5">
                <span
                  className="font-display font-bold text-2xl text-foreground"
                  data-testid="salary-intel-competitiveness"
                >
                  {d.competitiveness_pct != null ? `${Math.round(d.competitiveness_pct)}%` : "—"}
                </span>
                {d.trend?.competitiveness_pct && (
                  <TrendPill
                    delta={d.trend.competitiveness_pct.delta}
                    format="percent"
                    size="xs"
                    testId="salary-intel-comp-trend"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Percentile band visualization */}
          {d.band && (
            <div className="mb-6" data-testid="salary-intel-band">
              <div className="relative h-2.5 rounded-full bg-secondary overflow-hidden">
                {/* Gradient fill from p25 → p90 */}
                <div
                  className="absolute inset-y-0 rounded-full bg-gradient-to-r from-[hsl(var(--warning))]/70 via-primary/80 to-[hsl(var(--success))]"
                  style={{
                    left: `${bandTick("p25")}%`,
                    right: `${100 - bandTick("p90")}%`,
                  }}
                />
                {/* User's position marker */}
                {markerPct != null && (
                  <motion.div
                    initial={{ scale: 0, y: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground border-2 border-background shadow-md"
                    style={{ left: `calc(${markerPct}% - 8px)` }}
                    data-testid="salary-intel-marker"
                    aria-label="Your salary marker"
                    title={`You: ${currencyFmt(d.current_salary)}`}
                  />
                )}
              </div>
              {/* Percentile ticks */}
              <div className="relative h-6 mt-1.5">
                {PERCENTILES.map((key) => {
                  const left = bandTick(key);
                  if (left == null) return null;
                  return (
                    <div
                      key={key}
                      className="absolute top-0 flex flex-col items-center"
                      style={{ left: `calc(${left}% - 16px)`, width: "32px" }}
                    >
                      <div className="h-1 w-px bg-border" />
                      <div className="font-mono text-[9px] uppercase text-muted-foreground mt-0.5">
                        {PERCENTILE_LABEL[key]}
                      </div>
                      <div className="font-mono text-[10px] font-semibold text-foreground">
                        {compactFmt(d.band[key])}
                      </div>
                    </div>
                  );
                })}
              </div>
              {d.band_estimated && (
                <div className="text-[10px] text-muted-foreground/80 mt-6">
                  P25 &amp; P75 interpolated from median &amp; top-10% market data.
                </div>
              )}
            </div>
          )}

          {/* Delta to next percentile */}
          {d.next_percentile && (
            <div
              className="rounded-lg border border-primary/30 bg-primary/[0.05] p-3.5 mb-4"
              data-testid="salary-intel-next-target"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-0.5">
                    Next milestone · {d.next_percentile.target}
                  </div>
                  <div className="font-display font-bold text-lg text-foreground leading-snug">
                    +{currencyFmt(d.next_percentile.delta_to_target)}{" "}
                    <span className="text-sm text-muted-foreground font-normal">
                      to reach {currencyFmt(d.next_percentile.target_amount)}
                    </span>
                  </div>
                </div>
                <DollarSign
                  size={20}
                  strokeWidth={1.75}
                  className="text-primary flex-shrink-0"
                />
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => nav("/app/negotiation")}
              className="gap-1.5"
              data-testid="salary-intel-negotiate"
            >
              <Sparkles size={13} strokeWidth={2} /> Get negotiation script
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => nav("/app/salary")}
              className="gap-1.5"
              data-testid="salary-intel-full-report"
            >
              Full report <ArrowRight size={13} strokeWidth={2} />
            </Button>
          </div>

          {/* Lifetime earnings sub-line */}
          {d.lifetime_earnings_projection != null && (
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
              <span className="text-muted-foreground">20-yr lifetime projection</span>
              <span
                className="font-mono font-semibold text-foreground"
                data-testid="salary-intel-lifetime"
              >
                {compactFmt(d.lifetime_earnings_projection)}
              </span>
            </div>
          )}
        </>
      )}
    </Widget>
  );
};
